import { productWrites } from "../../api/mutations"
import { useAccess } from "../../auth/rights"
import { ActionNotes } from "../../components/Note"
import { useAction } from "../../hooks/useAction"
import { useConfirm } from "../../hooks/useConfirm"

/**
 * Пояснение и кнопка пересборки коротких имён товаров для сайта.
 *
 * Генерация — `PUT /api/product/generate-short-names`
 * (`ProductController.cs:366-384` → `ProductGeneratedNameManager.cs:26-84`):
 * метод глобальный, проходит по всей таблице `Products`, группового варианта
 * у бэка нет. Та же кнопка в ECM лежит на форме переводчика
 * («Сгенерировать короткие имена продуктов», `TranslateForm.tsx`) и там же
 * даёт роли Admin выбор стратегии подстановки названия атрибута; без Admin
 * ECM всегда шлёт `ByAttributeShortNameAndAttributeName` — Рига этой роли не
 * имеет, поэтому выбора в интерфейсе нет, шлём то же самое значение.
 *
 * Результат не возвращается в ответе и не виден интерфейсу: `Products.GeneratedNameJson`
 * ни один доступный Риге метод `ecm` не отдаёт. Колонка «Имя на сайте» в
 * таблице ниже — не значение из базы, а клиентское превью по тому же
 * алгоритму (`previewSiteName` в `columns.ts`); нажатие кнопки его не меняет.
 *
 * Та же кнопка продублирована в справочнике переводов (`dictionary/SyncPanel.tsx`)
 * — операция глобальная и разом по всем языкам, а не про одну карточку группы,
 * так что дублирование в разделе, где живут другие такие же операции, логично.
 * `btn--warn` вместо `btn--primary` — оба места, действие редкое и весит дорого.
 */
export default function SiteNameHelp() {
	const { isManager } = useAccess()
	const action = useAction()
	const { ask, dialog } = useConfirm()

	const run = async () => {
		const yes = await ask({
			title: "Пересобрать короткие имена товаров по всей базе?",
			text: "Операция глобальная: пройдёт по всем товарам во всех группах, а не только в этой карточке. На большой базе выполняется долго. Отменить одним действием нельзя.",
			confirm: "Сгенерировать",
		})
		if (!yes) return
		void action.run(
			() => productWrites.generateShortNames(),
			"Короткие названия успешно сгенерированы"
		)
	}

	return (
		<div className="panel tp-columns">
			<div className="panel__head">Имя товара на сайте</div>
			<div className="panel__body">
				<p className="tp-help">
					Из значений важных атрибутов (звёздочка в заголовке колонки, порядок
					— в панели «Важные») ERP собирает короткое имя товара для сайта:
					название группы → важные атрибуты по приоритету → торговая марка →
					артикул. У товара, где хоть один важный атрибут пуст, имя не
					соберётся вовсе.
				</p>
				<p className="tp-help">
					Колонка «Имя на сайте» в таблице ниже — превью, посчитанное прямо в
					браузере по тем же данным, а не значение из базы; прочерк там значит
					то же самое — имя для этого товара не соберётся. Настоящее значение
					лежит в базе и обновляется только этой кнопкой — она пересобирает
					имена у всех товаров сразу, во всех группах, а не только в этой
					карточке.
				</p>

				<ActionNotes action={action} />

				<button
					className="btn btn--warn"
					disabled={!isManager || action.busy}
					title={
						isManager
							? "Пересобрать короткие имена всех товаров по всей базе"
							: "Доступно только контент-менеджерам"
					}
					onClick={() => void run()}
				>
					Сгенерировать короткие имена по всей базе
				</button>

				{dialog}
			</div>
		</div>
	)
}
